import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchControlPanelSummary } from '../api';

function isYmd(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
}

function toZagrebYmd(date = new Date()) {
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Zagreb',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return fmt.format(date);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function normalizePeriodKey(v) {
  const s = String(v || '').trim();
  if (!s) return 'today';
  const lower = s.toLowerCase();
  if (lower === 'thisweek' || lower === 'this_week') return 'this_week';
  if (lower === 'thismonth' || lower === 'this_month') return 'this_month';
  if (lower === 'custom') return 'custom';
  return 'today';
}

function getPeriodLabels(periodKey) {
  const p = normalizePeriodKey(periodKey);
  if (p === 'this_week') {
    return {
      overviewTitle: 'Week Overview',
      periodUiLabel: 'This Week',
      plannedLabel: 'Planned This Week',
      loggedLabel: 'Logged This Week',
      workloadTitle: 'Workload This Week',
      periodAriaLabel: 'This Week',
    };
  }

  if (p === 'this_month') {
    return {
      overviewTitle: 'Month Overview',
      periodUiLabel: 'This Month',
      plannedLabel: 'Planned This Month',
      loggedLabel: 'Logged This Month',
      workloadTitle: 'Workload This Month',
      periodAriaLabel: 'This Month',
    };
  }

  if (p === 'custom') {
    return {
      overviewTitle: 'Period Overview',
      periodUiLabel: 'Custom',
      plannedLabel: 'Planned In Period',
      loggedLabel: 'Logged In Period',
      workloadTitle: 'Workload In Period',
      periodAriaLabel: 'In Period',
    };
  }

  return {
    overviewTitle: 'Today Overview',
    periodUiLabel: 'Today',
    plannedLabel: 'Planned Today',
    loggedLabel: 'Logged Today',
    workloadTitle: 'Workload Today',
    periodAriaLabel: 'Today',
  };
}

function InfoTip({ label, text }) {
  const id = useId();
  if (!text) return null;

  return (
    <span className="relative inline-flex items-center group">
      <button
        type="button"
        aria-describedby={id}
        aria-label={`${String(label || 'Info')} info`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onKeyDown={(e) => {
          // Prevent Enter/Space from triggering parent row/card actions.
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-[var(--ui-border)] bg-[var(--ui-surface)] text-[10px] font-semibold leading-none text-[var(--ui-text-muted)] hover:bg-[var(--ui-surface2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-primary-soft)]"
      >
        i
      </button>
      <div
        id={id}
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 hidden w-64 -translate-x-1/2 rounded-md border border-[var(--ui-border)] bg-[var(--ui-surface)] px-3 py-2 text-xs text-[var(--ui-text)] group-hover:block group-focus-within:block"
      >
        {text}
      </div>
    </span>
  );
}

export default function ControlPanel() {
  const navigate = useNavigate();
  const DEFAULT_PERIOD = 'today';
  const PERIOD_STORAGE_KEY = 'dashboard_period';
  const CUSTOM_RANGE_STORAGE_KEY = 'dashboard_custom_range';

  const readStoredPeriod = () => {
    try {
      const v = localStorage.getItem(PERIOD_STORAGE_KEY);
      const s = String(v || '').trim();
      if (!s) return null;
      // Keep this aligned with the current UI toggle options.
      const normalized = normalizePeriodKey(s);
      if (!['today', 'this_week', 'this_month', 'custom'].includes(normalized)) return null;
      return normalized;
    } catch {
      return null;
    }
  };

  const readStoredCustomRange = () => {
    try {
      const raw = localStorage.getItem(CUSTOM_RANGE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const start = String(parsed?.start || '').slice(0, 10);
      const end = String(parsed?.end || '').slice(0, 10);
      if (!isYmd(start) || !isYmd(end) || end < start) return null;
      return { start, end };
    } catch {
      return null;
    }
  };

  const [period, setPeriod] = useState(() => readStoredPeriod() || DEFAULT_PERIOD); // today | this_week | this_month | custom
  const periodUi = normalizePeriodKey(period);
  const [customRange, setCustomRange] = useState(() => {
    const stored = readStoredCustomRange();
    if (stored) return stored;
    const today = toZagrebYmd(new Date());
    return { start: today, end: today };
  });

  const labels = useMemo(() => getPeriodLabels(periodUi), [periodUi]);
  const periodApi = periodUi;
  const requestSeqRef = useRef(0);

  const [workloadSort, setWorkloadSort] = useState({ key: 'gap', dir: 'desc' }); // key: person|planned|logged|gap

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const periodQueryString = useMemo(() => {
    const qs = new URLSearchParams();
    qs.set('period', String(periodApi || 'today'));
    if (periodApi === 'custom' && isYmd(customRange?.start) && isYmd(customRange?.end)) {
      qs.set('start', String(customRange.start));
      qs.set('end', String(customRange.end));
    }
    return qs.toString();
  }, [periodApi, customRange]);

  useEffect(() => {
    try {
      localStorage.setItem(PERIOD_STORAGE_KEY, String(period || DEFAULT_PERIOD));
    } catch {
      // Ignore storage failures (private mode, disabled storage, etc.)
    }
  }, [period]);

  useEffect(() => {
    try {
      if (customRange && isYmd(customRange.start) && isYmd(customRange.end) && customRange.end >= customRange.start) {
        localStorage.setItem(CUSTOM_RANGE_STORAGE_KEY, JSON.stringify({ start: customRange.start, end: customRange.end }));
      }
    } catch {
      // Ignore storage failures.
    }
  }, [customRange]);

  const safeNavigate = (to) => {
    const raw = String(to || '').trim();
    if (!raw) return navigate('/people');
    const pathname = raw.split('?')[0] || '';
    const allowed = new Set(['/people', '/people-logs', '/reports', '/dashboard', '/control-panel', '/projects', '/calendar', '/tasks']);
    if (!allowed.has(pathname)) return navigate('/people');
    return navigate(raw);
  };

  const navTargets = useMemo(() => {
    const people = (qs) => `/people?${qs}&${periodQueryString}`;

    // Routing detection (based on existing App routes in this codebase):
    // - Dedicated tasks entry exists as /tasks (shim to /projects while preserving query params).
    const tasksRouteExists = true;

    const overdueTasksTarget = tasksRouteExists
      ? `/tasks?filter=overdue&${periodQueryString}`
      : `/dashboard?tab=tasks&filter=overdue&${periodQueryString}`;

    return {
      planned: people('filter=planned'),
      logged: `/reports?${periodQueryString}`,
      utilization: people('sort=utilization_desc'),
      overloaded: people('filter=overloaded'),
      empty: people('filter=empty'),
      overdueTasks: overdueTasksTarget,
      missingLogs: people('filter=missing_logs'),
    };
  }, [periodQueryString]);

  const personDetailTarget = (userId) => {
    const uid = userId != null ? String(userId) : '';
    if (!uid) return '/people';
    // Existing route in this codebase: /people/:personId
    return `/people/${encodeURIComponent(uid)}`;
  };

  useEffect(() => {
    const seq = ++requestSeqRef.current;
    const controller = new AbortController();

    setLoading(true);
    setError('');

    (async () => {
      try {
        const data = await fetchControlPanelSummary(periodApi, {
          start: periodApi === 'custom' ? customRange?.start : null,
          end: periodApi === 'custom' ? customRange?.end : null,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (requestSeqRef.current !== seq) return;
        setSummary(data || null);
      } catch (e) {
        // Ignore aborts; they are expected on period changes / StrictMode double-invoke.
        const name = String(e?.name || '').trim();
        if (name === 'AbortError') return;
        if (controller.signal.aborted) return;
        if (requestSeqRef.current !== seq) return;
        setError(String(e?.message || 'Failed to load summary'));
      } finally {
        if (controller.signal.aborted) return;
        if (requestSeqRef.current !== seq) return;
        setLoading(false);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [periodApi, customRange?.start, customRange?.end]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!summary?.debug) return;
    console.debug('[Workforce Overview metrics]', summary.debug);
  }, [summary]);

  const kpis = useMemo(() => {
    const planned = summary?.plannedTotal ?? summary?.plannedToday;
    const loggedPlanned = summary?.loggedPlannedTotal;
    const loggedOther = summary?.loggedOtherTotal;
    const loggedTotal = summary?.loggedTotal ?? summary?.loggedToday;
    const utilization = summary?.utilization;
    const overloaded = summary?.overloadedCount;
    const empty = summary?.emptySlotsCount;

    const fmtHours = (v) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : '—');
    const fmtPct = (v) => (typeof v === 'number' && Number.isFinite(v) ? `${Math.round(v)}%` : '—');
    const fmtCount = (v) => (typeof v === 'number' && Number.isFinite(v) ? String(v) : '—');

    return [
      {
        label: labels.plannedLabel,
        value: fmtHours(planned),
        helpText: 'Total planned hours in the selected period.',
        ariaLabel: `Open planned people (${labels.periodAriaLabel})`,
        onClick: () => safeNavigate(navTargets.planned),
      },
      {
        label: 'Logged (Planned)',
        value: fmtHours(loggedPlanned),
        helpText: 'Hours logged against planned assignment work in the selected period.',
        ariaLabel: `Open reports (${labels.periodAriaLabel})`,
        onClick: () => safeNavigate(navTargets.logged),
      },
      {
        label: 'Logged (Other)',
        value: fmtHours(loggedOther),
        helpText: 'Hours logged as other work in the selected period.',
        ariaLabel: `Open reports (${labels.periodAriaLabel})`,
        onClick: () => safeNavigate(navTargets.logged),
      },
      {
        label: 'Logged (Total)',
        value: fmtHours(loggedTotal),
        helpText: 'All logged hours in the selected period, including planned and other work.',
        ariaLabel: `Open reports (${labels.periodAriaLabel})`,
        onClick: () => safeNavigate(navTargets.logged),
      },
      {
        label: 'Utilization',
        value: fmtPct(utilization),
        helpText: '(Logged / Planned) * 100 for the selected period.',
        ariaLabel: `Open people sorted by utilization (${labels.periodAriaLabel})`,
        onClick: () => safeNavigate(navTargets.utilization),
      },
      {
        label: 'Overloaded',
        value: fmtCount(overloaded),
        helpText: 'Number of people with more than a full-time planned load in the selected period.',
        ariaLabel: `Open overloaded people (${labels.periodAriaLabel})`,
        onClick: () => safeNavigate(navTargets.overloaded),
      },
      {
        label: 'Empty Slots',
        value: fmtCount(empty),
        helpText: 'Number of people with 0 planned hours in the selected period.',
        ariaLabel: `Open people with empty slots (${labels.periodAriaLabel})`,
        onClick: () => safeNavigate(navTargets.empty),
      },
    ];
  }, [summary, labels, navTargets]);

  const alerts = useMemo(() => {
    const fmtCount = (v) => (typeof v === 'number' && Number.isFinite(v) ? String(v) : '—');
    return [
      {
        key: 'overloaded',
        label: 'Overloaded people',
        value: fmtCount(summary?.overloadedCount),
        helpText: 'Number of people with more than a full-time planned load in the selected period.',
        ariaLabel: `Open overloaded people (${labels.periodAriaLabel})`,
        onOpen: () => safeNavigate(navTargets.overloaded),
      },
      {
        key: 'overdue',
        label: 'Overdue tasks',
        value: fmtCount(summary?.overdueTasksCount),
        helpText: 'Tasks with due date before today and not marked as done.',
        ariaLabel: `Open overdue tasks (${labels.periodAriaLabel})`,
        onOpen: () => safeNavigate(navTargets.overdueTasks),
        onRowOpen: () => safeNavigate(navTargets.overdueTasks),
      },
      {
        key: 'missing_logs',
        label: 'Missing logs (yesterday)',
        value: fmtCount(summary?.missingLogsCount),
        helpText: 'People who had planned hours yesterday but logged 0.',
        ariaLabel: `Open missing logs (${labels.periodAriaLabel})`,
        onOpen: () => safeNavigate(navTargets.missingLogs),
      },
    ];
  }, [summary, labels, navTargets]);

  const sortedWorkload = useMemo(() => {
    const rows = Array.isArray(summary?.workloadToday) ? summary.workloadToday : [];
    const indexed = rows.map((r, idx) => ({ r, idx }));

    const key = String(workloadSort?.key || 'gap');
    const dir = String(workloadSort?.dir || 'desc') === 'asc' ? 'asc' : 'desc';
    const sign = dir === 'asc' ? 1 : -1;

    const cmpPerson = (a, b) => {
      const an = String(a?.name || '');
      const bn = String(b?.name || '');
      return an.localeCompare(bn, undefined, { sensitivity: 'base' });
    };
    const cmpNum = (av, bv) => {
      const a = Number(av);
      const b = Number(bv);
      const aa = Number.isFinite(a) ? a : -Infinity;
      const bb = Number.isFinite(b) ? b : -Infinity;
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    const numOrZero = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const gapOf = (row) => numOrZero(row?.planned) - numOrZero(row?.logged);

    const cmp = (a, b) => {
      if (key === 'person') return cmpPerson(a, b) * sign;
      if (key === 'logged') return cmpNum(a?.logged, b?.logged) * sign;
      if (key === 'gap') return cmpNum(gapOf(a), gapOf(b)) * sign;
      return cmpNum(a?.planned, b?.planned) * sign;
    };

    indexed.sort((aa, bb) => {
      const c = cmp(aa.r, bb.r);
      if (c !== 0) return c;
      // Stable sort
      return aa.idx - bb.idx;
    });

    return indexed.map(x => x.r);
  }, [summary, workloadSort]);

  const setSortKey = (key) => {
    setWorkloadSort((prev) => {
      const prevKey = String(prev?.key || 'planned');
      const prevDir = String(prev?.dir || 'desc') === 'asc' ? 'asc' : 'desc';
      if (prevKey === key) {
        return { key, dir: prevDir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: key === 'person' ? 'asc' : 'desc' };
    });
  };

  const sortIndicator = (key) => {
    if (String(workloadSort?.key || '') !== key) return '';
    return String(workloadSort?.dir || 'desc') === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--ui-text)]">Workforce Overview</h1>
          <p className="mt-1 text-sm text-[var(--ui-text-muted)]">{labels.periodUiLabel}</p>
          {loading && (
            <p className="mt-2 text-xs text-[var(--ui-text-muted)]">Loading…</p>
          )}
          {!loading && error && (
            <p className="mt-2 text-xs text-red-600">{error}</p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center rounded-lg border border-[var(--ui-border)] bg-[var(--ui-surface)] p-1">
            <button
              type="button"
              onClick={() => setPeriod('today')}
              aria-pressed={periodUi === 'today'}
              className={
                'ui-btn px-3 py-1.5 rounded text-sm border ' +
                (periodUi === 'today'
                  ? 'border-transparent bg-[var(--ui-primary-soft)] text-[var(--ui-text)]'
                  : 'border-transparent bg-transparent text-[var(--ui-text-muted)]')
              }
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setPeriod('this_week')}
              aria-pressed={periodUi === 'this_week'}
              className={
                'ui-btn px-3 py-1.5 rounded text-sm border ' +
                (periodUi === 'this_week'
                  ? 'border-transparent bg-[var(--ui-primary-soft)] text-[var(--ui-text)]'
                  : 'border-transparent bg-transparent text-[var(--ui-text-muted)]')
              }
            >
              This Week
            </button>
            <button
              type="button"
              onClick={() => setPeriod('this_month')}
              aria-pressed={periodUi === 'this_month'}
              className={
                'ui-btn px-3 py-1.5 rounded text-sm border ' +
                (periodUi === 'this_month'
                  ? 'border-transparent bg-[var(--ui-primary-soft)] text-[var(--ui-text)]'
                  : 'border-transparent bg-transparent text-[var(--ui-text-muted)]')
              }
            >
              This Month
            </button>
            <button
              type="button"
              onClick={() => setPeriod('custom')}
              aria-pressed={periodUi === 'custom'}
              className={
                'ui-btn px-3 py-1.5 rounded text-sm border ' +
                (periodUi === 'custom'
                  ? 'border-transparent bg-[var(--ui-primary-soft)] text-[var(--ui-text)]'
                  : 'border-transparent bg-transparent text-[var(--ui-text-muted)]')
              }
            >
              Custom
            </button>
          </div>
          {periodUi === 'custom' && (
            <div className="flex items-center gap-2 text-xs text-[var(--ui-text-muted)]">
              <label className="inline-flex items-center gap-1">
                <span>From</span>
                <input
                  type="date"
                  value={isYmd(customRange?.start) ? customRange.start : ''}
                  onChange={(e) => {
                    const nextStart = String(e.target.value || '').slice(0, 10);
                    setCustomRange((prev) => {
                      if (!isYmd(nextStart)) return prev;
                      const curEnd = isYmd(prev?.end) ? prev.end : nextStart;
                      const end = (isYmd(curEnd) && curEnd >= nextStart) ? curEnd : nextStart;
                      return { start: nextStart, end };
                    });
                  }}
                  className="h-8 rounded-md border border-[var(--ui-border)] bg-[var(--ui-surface)] px-2 text-[var(--ui-text)]"
                />
              </label>
              <label className="inline-flex items-center gap-1">
                <span>To</span>
                <input
                  type="date"
                  value={isYmd(customRange?.end) ? customRange.end : ''}
                  onChange={(e) => {
                    const nextEnd = String(e.target.value || '').slice(0, 10);
                    setCustomRange((prev) => {
                      if (!isYmd(nextEnd)) return prev;
                      const curStart = isYmd(prev?.start) ? prev.start : nextEnd;
                      const start = (isYmd(curStart) && curStart <= nextEnd) ? curStart : nextEnd;
                      return { start, end: nextEnd };
                    });
                  }}
                  className="h-8 rounded-md border border-[var(--ui-border)] bg-[var(--ui-surface)] px-2 text-[var(--ui-text)]"
                />
              </label>
            </div>
          )}
        </div>
      </header>

      {/* Overview */}
      <section className="rounded-lg border border-[var(--ui-border)] bg-[var(--ui-surface2)] p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--ui-text)]">{labels.overviewTitle}</h2>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
          {kpis.map((kpi) => (
            <button
              key={kpi.label}
              type="button"
              onClick={kpi.onClick}
              aria-label={kpi.ariaLabel}
              className="rounded-lg bg-[var(--ui-surface)] p-4 text-left cursor-pointer hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-primary-soft)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ui-surface2)]"
            >
              <div className="text-xs font-medium tracking-wide text-[var(--ui-text-muted)] inline-flex items-center">
                <span>{kpi.label}</span>
                <InfoTip label={kpi.label} text={kpi.helpText} />
              </div>
              <div className="mt-2 text-3xl font-semibold leading-none tabular-nums text-[var(--ui-text)]">
                {loading ? '—' : (kpi.value ?? '—')}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Critical Alerts */}
      <section className="rounded-lg border border-[var(--ui-border)] bg-[var(--ui-surface)] p-5">
        <h2 className="text-sm font-semibold text-[var(--ui-text)]">Critical Alerts</h2>

        <div className="mt-4 overflow-hidden rounded-lg border border-[var(--ui-border)] bg-[var(--ui-surface)]">
          {alerts.map((row) => (
            <div
              key={row.key}
              role={row.onRowOpen ? 'button' : undefined}
              tabIndex={row.onRowOpen ? 0 : undefined}
              onClick={row.onRowOpen ? row.onRowOpen : undefined}
              onKeyDown={row.onRowOpen ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  row.onRowOpen();
                }
              } : undefined}
              className={
                'flex items-center justify-between px-4 py-3 bg-[var(--ui-surface)]' +
                (row.onRowOpen ? ' cursor-pointer hover:bg-[var(--ui-surface2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-primary-soft)]' : '')
              }
            >
              <div className="min-w-0">
                <div className="text-sm text-[var(--ui-text)] inline-flex items-center">
                  <span>{row.label}</span>
                  <InfoTip label={row.label} text={row.helpText} />
                </div>
                <div className="mt-0.5 text-xs text-[var(--ui-text-muted)]">{loading ? '—' : row.value}</div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  row.onOpen && row.onOpen();
                }}
                aria-label={row.ariaLabel}
                className="ui-btn px-3 py-1.5 rounded text-sm border border-[var(--ui-border)] bg-[var(--ui-surface)] text-[var(--ui-text)]"
              >
                Open
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Workload */}
      <section className="rounded-lg border border-[var(--ui-border)] bg-[var(--ui-surface)] p-5">
        <h2 className="text-sm font-semibold text-[var(--ui-text)]">{labels.workloadTitle}</h2>

        <div className="mt-4 overflow-hidden rounded-lg border border-[var(--ui-border)] bg-[var(--ui-surface)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--ui-surface2)] text-[var(--ui-text)]">
              <tr>
                <th className="px-4 py-2 text-left font-semibold" aria-sort={workloadSort.key === 'person' ? (workloadSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button
                    type="button"
                    onClick={() => setSortKey('person')}
                    aria-label={`Sort by person ${sortIndicator('person') ? `(currently ${workloadSort.dir})` : ''}`}
                    className="inline-flex items-center gap-1 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-primary-soft)] rounded"
                  >
                    <span>Person</span>
                    <span className="text-xs text-[var(--ui-text-muted)]">{sortIndicator('person')}</span>
                  </button>
                </th>
                <th className="px-4 py-2 text-right font-semibold" aria-sort={workloadSort.key === 'planned' ? (workloadSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button
                    type="button"
                    onClick={() => setSortKey('planned')}
                    aria-label={`Sort by planned ${sortIndicator('planned') ? `(currently ${workloadSort.dir})` : ''}`}
                    className="inline-flex w-full items-center justify-end gap-1 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-primary-soft)] rounded"
                  >
                    <span>Planned</span>
                    <span className="text-xs text-[var(--ui-text-muted)]">{sortIndicator('planned')}</span>
                  </button>
                </th>
                <th className="px-4 py-2 text-right font-semibold" aria-sort={workloadSort.key === 'logged' ? (workloadSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button
                    type="button"
                    onClick={() => setSortKey('logged')}
                    aria-label={`Sort by logged ${sortIndicator('logged') ? `(currently ${workloadSort.dir})` : ''}`}
                    className="inline-flex w-full items-center justify-end gap-1 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-primary-soft)] rounded"
                  >
                    <span>Logged Total</span>
                    <span className="text-xs text-[var(--ui-text-muted)]">{sortIndicator('logged')}</span>
                  </button>
                </th>
                <th className="px-4 py-2 text-right font-semibold" aria-sort={workloadSort.key === 'gap' ? (workloadSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button
                    type="button"
                    onClick={() => setSortKey('gap')}
                    aria-label={`Sort by gap ${sortIndicator('gap') ? `(currently ${workloadSort.dir})` : ''}`}
                    className="inline-flex w-full items-center justify-end gap-1 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-primary-soft)] rounded"
                  >
                    <span>Gap</span>
                    <span className="text-xs text-[var(--ui-text-muted)]">{sortIndicator('gap')}</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-3 text-[var(--ui-text-muted)]" colSpan={4}>Loading…</td>
                </tr>
              ) : (Array.isArray(sortedWorkload) && sortedWorkload.length > 0 ? (
                sortedWorkload.map((r) => (
                  <tr
                    key={String(r.userId)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${String(r.name || 'person')}`}
                    onClick={() => navigate(personDetailTarget(r.userId))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(personDetailTarget(r.userId));
                      }
                    }}
                    className="border-t border-[var(--ui-border)] cursor-pointer hover:bg-[var(--ui-surface2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-primary-soft)]"
                  >
                    <td className="px-4 py-2 text-[var(--ui-text)]">{r.name || '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[var(--ui-text)]">{typeof r.planned === 'number' ? r.planned.toFixed(2) : '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[var(--ui-text)]">{typeof (r.loggedTotal ?? r.logged) === 'number' ? Number(r.loggedTotal ?? r.logged).toFixed(2) : '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[var(--ui-text)]">{((Number.isFinite(Number(r.planned)) ? Number(r.planned) : 0) - (Number.isFinite(Number(r.loggedTotal ?? r.logged)) ? Number(r.loggedTotal ?? r.logged) : 0)).toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-3 text-[var(--ui-text-muted)]" colSpan={4}>No data</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
